import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { FormField, FormLabel, FormSelect, FormInput, Button } from '../ui';
import userAccountService from '../../services/user-account.service';
import messageQueueService from '../../services/messageQueue.service';
import type { TicketFilters as TicketFiltersType } from '../../types/ticket';

interface TicketFiltersProps {
  onFilter: (filters: TicketFiltersType) => void;
  initialFilters?: TicketFiltersType;
}

export const TicketFilters: React.FC<TicketFiltersProps> = ({
  onFilter,
  initialFilters,
}) => {
  const [status, setStatus] = useState(initialFilters?.status || '');
  const [contact, setContact] = useState(initialFilters?.contact || '');
  const [userAccountId, setUserAccountId] = useState<string>(
    initialFilters?.user_account_id?.toString() || ''
  );
  const [queueId, setQueueId] = useState<string>(
    initialFilters?.queue_id?.toString() || ''
  );
  const [startDate, setStartDate] = useState(initialFilters?.start_date || '');
  const [endDate, setEndDate] = useState(initialFilters?.end_date || '');
  const [ticketId, setTicketId] = useState<string>(
    initialFilters?.ticket_id?.toString() || ''
  );

  const { data: userAccounts = [] } = useQuery({
    queryKey: ['user-accounts'],
    queryFn: () => userAccountService.getAccounts(),
  });

  const { data: queues = [] } = useQuery({
    queryKey: ['message-queues'],
    queryFn: () => messageQueueService.getQueues(),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onFilter({
      status: status ? (status as 'open' | 'pending' | 'closed') : undefined,
      contact: contact || undefined,
      user_account_id: userAccountId ? Number(userAccountId) : undefined,
      queue_id: queueId ? Number(queueId) : undefined,
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      ticket_id: ticketId ? Number(ticketId) : undefined,
    });
  };

  const handleClear = () => {
    setStatus('');
    setContact('');
    setUserAccountId('');
    setQueueId('');
    setStartDate('');
    setEndDate('');
    setTicketId('');
    onFilter({});
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 mb-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <FormField>
          <FormLabel htmlFor="ticket_id">ID do Ticket</FormLabel>
          <FormInput
            id="ticket_id"
            type="number"
            value={ticketId}
            onChange={(e) => setTicketId(e.target.value)}
            placeholder="Buscar por ID"
            min={1}
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="status">Status</FormLabel>
          <FormSelect
            id="status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">Todos os status</option>
            <option value="open">Aberto</option>
            <option value="pending">Pendente</option>
            <option value="closed">Fechado</option>
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="contact">Contato (Telefone)</FormLabel>
          <FormInput
            id="contact"
            type="text"
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            placeholder="Pesquisar por telefone"
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="user_account_id">Atendente</FormLabel>
          <FormSelect
            id="user_account_id"
            value={userAccountId}
            onChange={(e) => setUserAccountId(e.target.value)}
          >
            <option value="">Todos os atendentes</option>
            {userAccounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="queue_id">Fila</FormLabel>
          <FormSelect
            id="queue_id"
            value={queueId}
            onChange={(e) => setQueueId(e.target.value)}
          >
            <option value="">Todas as filas</option>
            {queues.map((queue) => (
              <option key={queue.id} value={queue.id}>
                {queue.name}
              </option>
            ))}
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="start_date">Data Inicial</FormLabel>
          <FormInput
            id="start_date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="end_date">Data Final</FormLabel>
          <FormInput
            id="end_date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </FormField>
      </div>

      <div className="mt-4 flex gap-2">
        <Button type="submit" className="flex-1">
          <Search className="h-4 w-4 mr-2" />
          Filtrar
        </Button>
        <Button type="button" variant="secondary" onClick={handleClear}>
          <X className="h-4 w-4" />
        </Button>
      </div>
    </form>
  );
};
